import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError, type CreateJobResponse } from '../../api/client'
import {
  CreateJobRequestSchema,
  JobTypeSchema,
  RiskLevelSchema,
  type JobType,
  type RiskLevel,
} from '@agent/shared'

// ISSUE #10: New Job フォーム。type / project / risk_level / input(JSON textarea) を
// バリデーションして POST /api/jobs に渡す。成功時に /jobs/:id へ遷移。
const JOB_TYPES = JobTypeSchema.options
const RISK_LEVELS = RiskLevelSchema.options

export function NewJobPage() {
  const navigate = useNavigate()
  const [type, setType] = useState<JobType>('chat')
  const [project, setProject] = useState('default')
  const [risk, setRisk] = useState<RiskLevel>('low')
  const [message, setMessage] = useState('hello')
  const [inputJson, setInputJson] = useState('')
  const [useJson, setUseJson] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const mutation = useMutation<CreateJobResponse, unknown, void>({
    mutationFn: async () => {
      setFormError(null)

      let input: Record<string, unknown>
      if (useJson) {
        if (inputJson.trim().length === 0) {
          throw new Error('Input JSON is empty.')
        }
        let parsed: unknown
        try {
          parsed = JSON.parse(inputJson)
        } catch (e) {
          throw new Error(`Input JSON is invalid: ${(e as Error).message}`)
        }
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('Input must be a JSON object.')
        }
        input = parsed as Record<string, unknown>
      } else {
        input = { message }
      }

      const candidate = {
        type,
        project,
        risk_level: risk,
        input,
      }
      const parsed = CreateJobRequestSchema.safeParse(candidate)
      if (!parsed.success) {
        throw new Error(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '))
      }
      return api.createJob(parsed.data)
    },
    onSuccess: (resp) => {
      navigate(`/jobs/${resp.id}`)
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      setFormError(msg)
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    mutation.mutate()
  }

  return (
    <section className="max-w-2xl">
      <h1 className="text-2xl font-semibold">New Job</h1>
      <p className="mt-1 text-sm text-slate-500">
        Create a job. It will be queued and picked up by the next available executor.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Type</span>
            <select
              className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
              value={type}
              onChange={(e) => setType(e.target.value as JobType)}
            >
              {JOB_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Risk level</span>
            <select
              className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
              value={risk}
              onChange={(e) => setRisk(e.target.value as RiskLevel)}
            >
              {RISK_LEVELS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Project</span>
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            required
          />
        </label>

        <div className="rounded border border-slate-200 bg-white p-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useJson}
              onChange={(e) => setUseJson(e.target.checked)}
            />
            <span>Use raw JSON for input</span>
          </label>

          {useJson ? (
            <label className="mt-3 block">
              <span className="text-sm font-medium text-slate-700">Input (JSON object)</span>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
                rows={8}
                placeholder='{"message": "hello"}'
                value={inputJson}
                onChange={(e) => setInputJson(e.target.value)}
              />
            </label>
          ) : (
            <label className="mt-3 block">
              <span className="text-sm font-medium text-slate-700">Message</span>
              <textarea
                className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                Sent as <code className="font-mono">{`{ "message": "..." }`}</code>.
              </p>
            </label>
          )}
        </div>

        {formError && (
          <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className="rounded border border-slate-300 bg-white px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating…' : 'Create Job'}
          </button>
        </div>
      </form>
    </section>
  )
}
